import { Controller } from "@hotwired/stimulus"

// Cascading selects: trade → category → subcategory.
//
// Usage on any wrapper element:
//   data-controller="equipment-cascading-select"
//   data-equipment-cascading-select-building-complex-id-value="<id>"
//   data-equipment-cascading-select-mode-value="form" (default) | "filter"
//
// Targets:
//   trade       — select for EquipmentTrade
//   category    — select for EquipmentCategory (data-blank-label optional)
//   subcategory — select for EquipmentSubcategory (data-blank-label optional)
//
// Modes:
//   "form"   — dependent selects start disabled; options loaded via AJAX on demand.
//              Edit form: restores selections from data-selected-id on connect().
//   "filter" — selects are never disabled; all options shown on load, narrowed on change.
//              Loads full category/subcategory lists via AJAX once on connect().

export default class extends Controller {
  static targets = ["trade", "category", "subcategory"]
  static values  = {
    buildingComplexId: String,
    mode: { type: String, default: "form" }
  }

  // All categories/subcategories cached after initial load (filter mode only)
  _allCategories    = []
  _allSubcategories = []

  async connect() {
    if (this.modeValue === "filter") {
      await this._initFilter()
    } else {
      await this._restoreFormSelections()
    }
  }

  async tradeChanged(event) {
    if (this.modeValue === "filter") {
      this._filterByTrade(event.target.value)
    } else {
      await this._loadCategories(event.target.value, null)
    }
  }

  async categoryChanged(event) {
    if (this.modeValue === "filter") {
      this._filterByCategory(event.target.value)
    } else {
      await this._loadSubcategories(event.target.value, null)
    }
  }

  // ── filter mode ─────────────────────────────────────────────────────────────

  async _initFilter() {
    const base = `/complexes/${this.buildingComplexIdValue}/equipments`
    ;[this._allCategories, this._allSubcategories] = await Promise.all([
      this._fetchJSON(`${base}/categories_for_trade`),
      this._fetchJSON(`${base}/subcategories_for_category`)
    ])

    const tradeId    = this.tradeTarget.value
    const categoryId = this.categoryTarget.value
    const subcatId   = this.subcategoryTarget.value

    const cats = tradeId ? this._allCategories.filter(c => String(c.trade_id) === tradeId)
                         : this._allCategories
    this.#renderOptions(this.categoryTarget, cats, categoryId)

    const subs = this._subcatsForState(tradeId, categoryId)
    this.#renderOptions(this.subcategoryTarget, subs, subcatId)
  }

  _filterByTrade(tradeId) {
    const prevCategory = this.categoryTarget.value
    const cats = tradeId ? this._allCategories.filter(c => String(c.trade_id) === tradeId)
                         : this._allCategories

    const keepCategory = cats.some(c => String(c.id) === prevCategory)
    this.#renderOptions(this.categoryTarget, cats, keepCategory ? prevCategory : null)

    const subs = this._subcatsForState(tradeId, keepCategory ? prevCategory : null)
    this.#renderOptions(this.subcategoryTarget, subs, null)
  }

  _filterByCategory(categoryId) {
    const subs = this._subcatsForState(this.tradeTarget.value, categoryId)
    this.#renderOptions(this.subcategoryTarget, subs, null)
  }

  _subcatsForState(tradeId, categoryId) {
    if (categoryId) {
      return this._allSubcategories.filter(s => String(s.category_id) === String(categoryId))
    }
    if (tradeId) {
      const catIds = new Set(
        this._allCategories.filter(c => String(c.trade_id) === tradeId).map(c => String(c.id))
      )
      return this._allSubcategories.filter(s => catIds.has(String(s.category_id)))
    }
    return this._allSubcategories
  }

  #renderOptions(select, items, selectedId) {
    const blankLabel = select.dataset.blankLabel ?? ""
    select.innerHTML = `<option value="">${blankLabel}</option>`
    items.forEach(({ id, name }) => {
      const opt = document.createElement("option")
      opt.value = id
      opt.textContent = name
      if (String(id) === String(selectedId)) opt.selected = true
      select.appendChild(opt)
    })
  }

  // ── form mode (AJAX) ────────────────────────────────────────────────────────

  async _restoreFormSelections() {
    const tradeId    = this.tradeTarget.value
    const categoryId = this.categoryTarget.dataset.selectedId
    const subcatId   = this.subcategoryTarget.dataset.selectedId

    if (tradeId)    await this._loadCategories(tradeId, categoryId)
    if (categoryId) await this._loadSubcategories(categoryId, subcatId)
  }

  async _loadCategories(tradeId, selectedId) {
    this._resetSelect(this.categoryTarget)
    this._resetSelect(this.subcategoryTarget)
    if (!tradeId) return

    const base  = `/complexes/${this.buildingComplexIdValue}/equipments`
    const items = await this._fetchJSON(`${base}/categories_for_trade?trade_id=${tradeId}`)
    this._fillSelect(this.categoryTarget, items, selectedId)
  }

  async _loadSubcategories(categoryId, selectedId) {
    this._resetSelect(this.subcategoryTarget)
    if (!categoryId) return

    const base  = `/complexes/${this.buildingComplexIdValue}/equipments`
    const items = await this._fetchJSON(`${base}/subcategories_for_category?category_id=${categoryId}`)
    this._fillSelect(this.subcategoryTarget, items, selectedId)
  }

  _resetSelect(select) {
    const blankLabel = select.dataset.blankLabel ?? ""
    select.innerHTML = `<option value="">${blankLabel}</option>`
    select.disabled = true
  }

  _fillSelect(select, items, selectedId) {
    items.forEach(({ id, name }) => {
      const opt = document.createElement("option")
      opt.value = id
      opt.textContent = name
      if (String(id) === String(selectedId)) opt.selected = true
      select.appendChild(opt)
    })
    select.disabled = items.length === 0
  }

  // ── shared ──────────────────────────────────────────────────────────────────

  async _fetchJSON(url) {
    try {
      const res = await fetch(url)
      return await res.json()
    } catch {
      return []
    }
  }
}
